import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, GridItem } from '@jutro/layout';
import _ from 'lodash';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { ehCommonMessages,  commonMessages as e1pCommonMessages  } from 'e1p-platform-translations';
import { EHSingleClauseComponentVM } from 'e1p-capability-policyjob-react';
import styles from './EHSideBySideMandatoryCoveragesComponent.module.scss';

function getPropertyCoverageSection(
    givenSectionCoverages, COLUMNS, translator, propertyCovSectionName, lobOfferings,
    quoteDataOfferings, onChangeSubmission, syncCoverages, changeSubmissionAndSync,
    isLoading, onValidate, hasExtendedAttributeEditability, policyState, isQuoteNamesVisible) {
    const rowHeaders = [''] // Need first cell to be empty
        .concat(givenSectionCoverages);
    const ROWS = rowHeaders.length;
    const numberOfCells = COLUMNS * ROWS;
    const tableContent = [];

    for (let cell = 0; cell <= numberOfCells - 1; cell++) {
        // There will always be 3 rows based on mock,
        //   but cols differ based on # of versions
        const rowNumber = Math.floor(cell / COLUMNS) + 1;
        const columnNumber = cell % COLUMNS;
        const OFFSET = 1; // array starts at 0 and first element is a header
        const offeringIndex = columnNumber - OFFSET;
        const path = `lobData.homeowners_EH.offerings.children[${offeringIndex}].coverages.coverages`;

        // first column - render row headers
        if (cell === 0) {
            rowHeaders.shift();
            tableContent.push(<GridItem
                className={rowNumber === ROWS ? styles.qDetailCellBottomLeftHeader : styles.qDetailCellLeftHeader}
                tag="div" key={`coverageGridItem${cell}`}>
                <span key={`coverageCell${cell}`}>
                    {translator(propertyCovSectionName)}
                </span>
            </GridItem>);
        }
        else if ((cell % COLUMNS === 0)) {
            tableContent.push(<GridItem
                className={rowNumber === ROWS ? styles.qDetailCellBottomLeftHeader : styles.qDetailCellLeftHeader}
                tag="div" key={`coverageGridItem${cell}`}>
                <span key={`coverageCell${cell}`}>
                    {rowHeaders.shift().name}
                </span>
            </GridItem>);
        }
        else if (rowNumber === 1) {
            tableContent.push(
                <span key={`quotename${cell}`} className={styles.qDetailCellVersionName}>
                    {isQuoteNamesVisible ? lobOfferings[cell - 1].branchName : ''}
                </span>
            );
        }
        else {
            const covHeader = givenSectionCoverages[rowNumber - 2];
            const clause = lobOfferings[offeringIndex].coverages.coverages.find(
                (coverage) => coverage.publicID === covHeader.publicID
            );
            const clauseIndex = lobOfferings[offeringIndex].coverages.coverages.findIndex(
                (coverage) => coverage.publicID === covHeader.publicID
            );

            if (clause) {
                const currentQuoteOffering = quoteDataOfferings
                    .find((quoteOffering) => quoteOffering.branchCode === lobOfferings[offeringIndex].branchCode);
                const isCurrentOfferingNotQuoted = currentQuoteOffering.status !== 'Quoted';

                tableContent.push(<GridItem
                    tag="div" key={`quotecell${cell}`}
                    className={rowNumber === ROWS ? styles.qDetailCellBottom : styles.qDetailCell}>
                    <EHSingleClauseComponentVM
                        value={clause}
                        path={`${path}.children[${clauseIndex}]`}
                        onChangeClause={onChangeSubmission}
                        onSyncCoverages={(value, termPath) => {
                            syncCoverages(
                                value, termPath, `lobData.homeowners_EH.offerings.children[${offeringIndex}]`,
                                `quoteData.offeredQuotes.children[${offeringIndex}]`, clause.publicID
                            );
                        }}
                        onChangeSubmissionAndSync={(value, termPath) => {
                            changeSubmissionAndSync(
                                value, termPath, `lobData.homeowners_EH.offerings.children[${offeringIndex}]`,
                                `quoteData.offeredQuotes.children[${offeringIndex}]`, clause.publicID
                            );
                        }}
                        loadingClause={isLoading}
                        idPrefex={clause.publicID}
                        labelTop
                        onValidate={onValidate}
                        isQuoteStale={isCurrentOfferingNotQuoted}
                        hasExtendedAttributeEditability={hasExtendedAttributeEditability}
                        policyState={policyState}
                        isSideBySideQuoteMode />
                </GridItem>);
            }
            else {
                tableContent.push(
                    <GridItem
                        tag="div" key={`quotecell${cell}`}
                        className={rowNumber === ROWS ? styles.qDetailCellBottom : styles.qDetailCell}>
                        <span className={styles.noCoverageText}>{translator(e1pCommonMessages.notApplicable)}</span>
                    </GridItem>
                );
            }

        }
    }

    tableContent.push(<GridItem
        tag="div" key={`quotecell${propertyCovSectionName}`}
        className={styles.coverageSectionBottomPadding}
        colSpan={COLUMNS} />);

    return tableContent;
}

function EHSideBySideMandatoryCoveragesComponent(props) {
    const {
        lobOfferings,
        onChangeSubmissionAndSync,
        onChangeSubmission,
        onSyncCoverages,
        onValidate,
        hasExtendedAttributeEditability,
        policyState,
        quoteDataOfferings
    } = props;

    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(undefined);
    const id = 'EHSideBySideMandatoryCoveragesComponent';
    const { isComponentValid } = useValidation(id);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, lobOfferings, onValidate, isComponentValid]);

    /** Callback from main quote page */
    const syncCoverages = useCallback((value, path, lobPath, quotePath, clauseID) => {
        setIsLoading(clauseID);

        if (onSyncCoverages) {
            onSyncCoverages(value, path, lobPath, quotePath).then(() => {
                setIsLoading(undefined);
            });
        }
    }, [onSyncCoverages]);

    /** Callback from main quote page */
    const changeSubmissionAndSync = useCallback((value, path, lobPath, quotePath, clauseID) => {
        setIsLoading(clauseID);

        if (onChangeSubmissionAndSync) {
            return Promise.resolve(onChangeSubmissionAndSync(value, path, lobPath, quotePath).then(() => {
                setIsLoading(undefined);
            }));
        }

        return false;
    }, [onChangeSubmissionAndSync]);

    /**
     * Will return unique set of coverage across the versions 
     * @returns {Array} list of objects - cov public ids
     */
    const getAllCoverage = useCallback(() => {
        const coverages = _.uniqBy(lobOfferings.flatMap((offering) => (
            offering.coverages.coverages
        )), 'publicID');

        return coverages;
    }, [lobOfferings]);

    const COLUMNS = lobOfferings.length + 1;
    const columnConfig = [];

    for (let column = 0; column < COLUMNS; column++) {
        let columnWidthToPush = '320px';

        if (column === 0) {
            columnWidthToPush = '210px';
        }

        columnConfig.push(columnWidthToPush);
    }

    // don't want to manipulate the actual lob data so making a copy
    const sectionICoverages = getAllCoverage().filter((cov) => cov.uigroup === 'Section I');
    const sectionICoverageTypeLabel = ehCommonMessages.sectionICoverage;

    const sectionICovTableContent = getPropertyCoverageSection(sectionICoverages, COLUMNS, translator, sectionICoverageTypeLabel,
        lobOfferings, quoteDataOfferings, onChangeSubmission, syncCoverages, changeSubmissionAndSync,
        isLoading, onValidate, hasExtendedAttributeEditability, policyState, true);

    const sectionIICoverages = getAllCoverage().filter((cov) => cov.uigroup === 'Section II');
    const sectionIICoverageTypeLabel = ehCommonMessages.sectionIICoverage;

    const sectionIICovTableContent = getPropertyCoverageSection(sectionIICoverages, COLUMNS, translator, sectionIICoverageTypeLabel,
        lobOfferings, quoteDataOfferings, onChangeSubmission, syncCoverages, changeSubmissionAndSync,
        isLoading, onValidate, hasExtendedAttributeEditability, policyState, false);

    const deductibles = getAllCoverage().filter((cov) => cov.uigroup === 'Deductibles');

    const deductiblesTableContent = getPropertyCoverageSection(deductibles, COLUMNS, translator, '',
        lobOfferings, quoteDataOfferings, onChangeSubmission, syncCoverages, changeSubmissionAndSync,
        isLoading, onValidate, hasExtendedAttributeEditability, policyState, true);

    const deductibleLabel = (
        <GridItem
            tag="div" key="quotecellDeductbleLabel"
            colSpan={COLUMNS}>
            <h4>{translator(ehCommonMessages.deductibles)}</h4>
        </GridItem>
    );
    let tableContent = sectionICovTableContent.concat(sectionIICovTableContent)

    if (!_.isEmpty(deductibles)) {
        tableContent = tableContent.concat(deductibleLabel).concat(deductiblesTableContent);
    }

    return (
        <div className={styles.tableSpacingAbove}>
            <h4>{translator(ehCommonMessages.propertyCoverages)}</h4>
            <Grid columns={columnConfig} gap="none">
                {tableContent}
            </Grid>
        </div>
    );
}

EHSideBySideMandatoryCoveragesComponent.propTypes = {
    lobOfferings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    quoteDataOfferings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    updateQdd: PropTypes.func.isRequired,
    updateQuoteVersionName: PropTypes.func.isRequired,
    setSelectedVersion: PropTypes.func.isRequired,
    onWithdrawSideBySideVersion: PropTypes.func.isRequired,
    policyState: PropTypes.string.isRequired,
    hasExtendedAttributeEditability: PropTypes.bool.isRequired,
    onValidate: PropTypes.func.isRequired
};

export default EHSideBySideMandatoryCoveragesComponent;
